import type { Series } from './types';
import type { Entities } from '@/components/TimeSeries';

export const groupByYearMonthWithTimestamp = (
  data: number[][]
): Record<number, number[]> => {
  const groupedData = {};

  data.forEach(([timestamp, value]) => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // Months are zero-based in JavaScript

    const firstDayOfMonth = new Date(year, month - 1, 1).getTime();

    if (!groupedData[firstDayOfMonth]) {
      groupedData[firstDayOfMonth] = [];
    }

    groupedData[firstDayOfMonth].push(value);
  });

  return groupedData;
};

export const normalizeByMonthlySumWithTimestamp = (seriesData: number[][]) => {
  const groupedData = groupByYearMonthWithTimestamp(seriesData);
  return Object.entries(groupedData)
    .map(([timestamp, values]) => {
      const sum = values.reduce((sum, value) => sum + value, 0);
      return [parseInt(timestamp), sum];
    })
    .sort((a, b) => a[0] - b[0]);
};

export const countMonths = (startTimestamp: number, endTimestamp: number) => {
  const startDate = new Date(startTimestamp);
  const endDate = new Date(endTimestamp);

  // Calculate year and month differences
  const yearsDifference = endDate.getFullYear() - startDate.getFullYear();
  const monthsDifference = endDate.getMonth() - startDate.getMonth();

  // Total months between the two dates
  const totalMonths = yearsDifference * 12 + monthsDifference + 1;

  return totalMonths;
};

export const mergeSeries = (data: Record<Series, number[][]>) => {
  const mergedData = Object.values(data).flat(1);
  return normalizeByMonthlySumWithTimestamp(mergedData);
};

export const calcMinMaxTimestamp = (groupedData: number[][]) => {
  // Current date
  const now = new Date();
  const seriesTimestamps = groupedData.map((n) => n[0]);
  const lastTimestamp = seriesTimestamps[seriesTimestamps.length - 1];
  const firstTimestamp = seriesTimestamps[0];

  // First day of the current month
  const firstDayCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const firstDayCurrentMonthTimestamp = firstDayCurrentMonth.getTime();

  // First day of the month 3 years before today
  const firstDayThreeYearsAfter = new Date(
    now.getFullYear() + 3,
    now.getMonth() - 1,
    1
  );
  const firstDayThreeYearsAfterTimestamp = firstDayThreeYearsAfter.getTime();
  const minX = Math.min(firstTimestamp, firstDayCurrentMonthTimestamp);
  const maxX = Math.max(lastTimestamp, firstDayThreeYearsAfterTimestamp);
  return {
    minX,
    maxX,
  };
};

export const calcChartInfo = (data: Record<Series, number[][]>) => {
  const groupedData = mergeSeries(data);

  const seriesValues = groupedData.map((n) => n[1]);
  const minY = Math.min(...seriesValues);
  const maxY = Math.max(...seriesValues);

  const dayZero = groupedData[0][0] || new Date().getTime();

  const { minX, maxX } = calcMinMaxTimestamp(groupedData);

  const chartColumnsCap = countMonths(minX, maxX);

  return { minX, maxX, minY, maxY, dayZero, chartColumnsCap };
};

type ConvertTimeseriesDataToStackDataParams<T extends string | number> = {
  seriesKeys: number[];
  barKeys: string[];
  entities: Entities<T>;
};

export const convertTimeseriesDataToStackData = <T extends string | number>({
  seriesKeys,
  barKeys,
  entities,
}: ConvertTimeseriesDataToStackDataParams<T>) => {
  return seriesKeys.reduce((acc, key) => {
    return {
      ...acc,
      [key]: barKeys.map((barKey) => entities[key][barKey]),
    };
  }, {});
};
